import { AsyncPipe } from '@angular/common';
import { AfterViewInit, ChangeDetectionStrategy, Component, CUSTOM_ELEMENTS_SCHEMA, ElementRef, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Keyboard, KeyboardInfo } from '@capacitor/keyboard';
import {
  AlertController, IonButton,
  IonButtons,
  IonContent,
  IonFooter,
  IonHeader,
  IonIcon,
  IonTitle,
  IonToolbar, LoadingController, ModalController, Platform
} from '@ionic/angular/standalone';
import { Subject } from 'rxjs';
import { delay, first, timeout } from 'rxjs/operators';
import { isQuestion, QuestionItemType, ServiceError, SlipDto, SlipService } from 'src/app/services/slip.service';
import { applyTimer } from 'src/app/services/timer';
import { Swiper, SwiperOptions } from 'swiper/types';
import { ModalBackButtonComponent } from '../../modal-back-button/modal-back-button.component';
import { ErrorLut, findError } from '../student-slip-modal.store';
import { SsqwFormSlideComponent } from './ssqw-form-slide/ssqw-form-slide.component';
import { Status } from './ssqw-form-slide/status';
import { SsqwInstructionsSlideComponent } from './ssqw-instructions-slide/ssqw-instructions-slide.component';
import { SsqwSummarySlideComponent } from './ssqw-summary-slide/ssqw-summary-slide.component';
import { StudentSlipQuestionsWizardStore } from './student-slip-questions-wizard.store';

export const questionsSubmitErrorLut: ErrorLut<{ message: string; canRetry: boolean; showReload: boolean }> = {
  0: {
    message: `We weren't able to submit your response because it looks like you're offline. <br /><br />Please check your wifi or mobile data connection and try again later.`,
    canRetry: true,
    showReload: false
  },
  404: {
    message: `This slip is no longer active for your child.<br /><br />Please contact the school for more information.`,
    canRetry: false,
    showReload: false
  },
  2000: {
    message: `This slip is no longer active for your child.<br /><br />Please contact the school for more information.`,
    canRetry: false,
    showReload: false
  },
  2001: {
    message: `This slip is no longer active for your child.<br /><br />Please contact the school for more information.`,
    canRetry: false,
    showReload: false
  },
  2007: {
    message: `Responses to these questions have already been submitted.<br /><br />Please tap 'Refresh' to update the slip.`,
    canRetry: false,
    showReload: true
  }
};

@Component({
  schemas: [CUSTOM_ELEMENTS_SCHEMA], // for swiper
  selector: 'app-student-slip-questions-wizard',
  templateUrl: './student-slip-questions-wizard.component.html',
  styleUrls: ['./student-slip-questions-wizard.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [StudentSlipQuestionsWizardStore],
  imports: [
    ModalBackButtonComponent,
    SsqwFormSlideComponent,
    SsqwInstructionsSlideComponent,
    SsqwSummarySlideComponent,
    AsyncPipe,
    IonHeader,
    IonToolbar,
    IonButtons,
    IonTitle,
    IonContent,
    IonFooter,
    IonButton,
    IonIcon
  ]
})
export class StudentSlipQuestionsWizardComponent implements OnInit, OnDestroy, AfterViewInit {
  @Input() items: QuestionItemType[];
  @Input() slipComponentInfo: { schoolId: number; studentId: number; slipId: number; componentId: number };

  @ViewChild('swiperRef', { static: false })
  set swiperRef(swiperRef: ElementRef | undefined) {
    if (swiperRef) {
      this._swiperRef = swiperRef;
    }
  }
  protected _swiperRef: ElementRef | undefined;
  swiper?: Swiper;

  @ViewChild('footer', { read: ElementRef }) private footerElement: ElementRef;

  slideItems$ = this.componentStore.slideItems$;
  onSummary$ = this.componentStore.onSummary$;
  summary$ = this.componentStore.summary$;
  footerModel$ = this.componentStore.footerModel$;

  keyboardDidShow = new Subject<KeyboardInfo>();
  keyboardDidShow$ = this.keyboardDidShow.asObservable();

  constructor(
    private readonly componentStore: StudentSlipQuestionsWizardStore,
    private slipService: SlipService,
    private modalController: ModalController,
    private loadingCtrl: LoadingController,
    private alertController: AlertController,
    private platform: Platform
  ) {}

  ngOnDestroy(): void {
    if (this.platform.is('ios')) {
      Keyboard.setAccessoryBarVisible({ isVisible: false });
    }
  }

  ngOnInit(): void {
    // Mixin cached response from store if any
    this.componentStore.init({ slipQuestionResponseId: this.slipQuestionResponseId, items: this.items });
    this.componentStore.configureKeyboard();

    // We'd like to scroll to bottom of page to show the control buttons when the Keyboard is shown
    // however we need to delay a bit for it to settle
    Keyboard.addListener('keyboardDidShow', (info: KeyboardInfo) => {
      this.keyboardDidShow.next(info);
      // timer(100, 0)
      //   .pipe(take(1))
      //   .subscribe(() => {
      //     // We use virtual slides which always has prev/next plus current so middle of three is current
      //     const activeSlide = this.swiperComponent.swiperRef.slides[1];
      //     activeSlide.scroll({
      //       top: activeSlide.scrollHeight,
      //       left: 0,
      //       behavior: 'smooth'
      //     });
      //   });
    });
    // We hide the footer when the keyboard is showing however there is no easy way to do this with ionic
    // so we use the following to redisplay the footer after keyboard disappears and use the input click
    // event to hide the footer (this avoid a glitch where the footer disappears/appears/disappears quickly)
    Keyboard.addListener('keyboardDidHide', () => {
      this.footerElement.nativeElement.style.display = 'block';
    });
    // Although we use the click event from the input element to hide the footer, a suspend/resume cycle
    // bypasses this so fallback to this method
    Keyboard.addListener('keyboardWillShow', () => {
      this.footerElement.nativeElement.style.display = 'none';
    });
  }

  ngAfterViewInit(): void {
    // https://github.com/nolimits4web/swiper/discussions/6384
    setTimeout(() => {
      this.initSwiper();
    }, 0);
  }

  trackByFn(index: number, item: any): number {
    return index;
  }

  private initSwiper() {
    const options: SwiperOptions = {
      threshold: 5,
      virtual: {
        enabled: true
      },
      scrollbar: {
        enabled: true
      }
    };

    const swiperEl = this._swiperRef.nativeElement;
    Object.assign(swiperEl, options);

    swiperEl.initialize();

    if (this.swiper) this.swiper.currentBreakpoint = false; // Breakpoint fixes
    this.swiper = this._swiperRef.nativeElement.swiper;

    this.swiper.off('slideChange'); // Avoid multiple subscription, in case you wish to call the `_initSwiper()` multiple time

    this.swiper.on('slideChange', () => { // Any change subscription you wish
      this.onSlideChanged();
    });
  }

  get slipQuestionResponseId(): string {
    return `${this.slipComponentInfo.studentId}:${this.slipComponentInfo.componentId}`;
  }

  isQuestionSlide(item: QuestionItemType): boolean {
    return isQuestion(item);
  }

  onSlideChanged(): void {
    this.componentStore.setItemIndex(this.swiper.activeIndex);
    this.componentStore.configureKeyboard();

    // Rmove focus and hence keyboard when using gesture between slides
    (document.activeElement as any).blur();
  }

  onDismiss(): void {
    this.modalController.dismiss();
  }

  onSlidePrev(): void {
    this.swiper.slidePrev(300);
  }

  onSlideNext(): void {
    this.swiper.slideNext(300);
  }

  onFormSlideStatus(status: Status, slide: number): void {
    this.componentStore.setItemStatus({ index: slide, status: status });
    this.componentStore.cacheState();
  }

  onSwitchSlide(slide: number): void {
    this.swiper.slideTo(slide, 300);
  }

  onInputClicked(container: HTMLElement, element: HTMLElement): void {
    // Ideally we'd use the keyboard will show event but doing this causes an
    // extra show/hide cycle
    this.footerElement.nativeElement.style.display = 'none';
    this.keyboardDidShow$.pipe(delay(100), first(), timeout(1000)).subscribe(
      (info: KeyboardInfo) => {
        // We use virtual slides which always has prev/next plus current so middle of three is current
        // except when at start where current is first slide
        const activeIndex = this.swiper.slides.length === 2 ? 0 : 1;
        const activeSlide = this.swiper.slides[activeIndex];
        const containerBoundingRect = container.getBoundingClientRect();
        const elementBoundingRect = element.getBoundingClientRect();
        const elementOffset = elementBoundingRect.top - containerBoundingRect.top;
        activeSlide.scrollBy({
          top: elementOffset,
          left: 0,
          behavior: 'auto'
        });
        console.log('got event ', elementOffset);
      },
      // May timeout if keyboard already up
      () => { /* ignore */ }
    );
  }

  async onSubmit(payload: any[]): Promise<void> {
    const loading = await this.loadingCtrl.create();
    loading.present().then((_) => {
      const apiCall = this.slipService.submitComponentResponse(
        this.slipComponentInfo.schoolId,
        this.slipComponentInfo.studentId,
        this.slipComponentInfo.slipId,
        this.slipComponentInfo.componentId,
        { answers: payload }
      );

      applyTimer(apiCall).subscribe(
        (result: SlipDto) => {
          loading.dismiss();
          this.modalController.dismiss({ slip: result });
        },
        async (error: ServiceError) => {
          loading.dismiss();
          if (error.stage === 0) {
            const messageDetail = findError(error.apiError?.ErrorCode ?? error.httpStatus, questionsSubmitErrorLut, {
              message: `We weren't able to submit your response. <br /><br />Please try again later.`,
              canRetry: false,
              showReload: false
            });

            const alert = await this.alertController.create({
              message: messageDetail.message,
              buttons: messageDetail.canRetry
                ? [
                    {
                      text: 'Cancel',
                      role: 'cancel'
                    },
                    {
                      text: 'Retry',
                      handler: () => {
                        this.onSubmit(payload);
                      }
                    }
                  ]
                : messageDetail.showReload
                  ? [
                      {
                        text: 'Refresh',
                        handler: () => {
                          this.modalController.dismiss({ reloadSlip: true });
                        }
                      }
                    ]
                  : ['Ok']
            });
            await alert.present();
          } else {
            // Submit success but slip load failed so tell caller that
            // we need to reload slip page
            this.modalController.dismiss({ reloadSlip: true });
          }
        }
      );
    });
  }
}
